import * as React from 'react';
import bind from '../bind';
import * as I18n from 'i18n-js';
import * as qs from 'qs';
import SanitizedRender from './sanitized_render';
import { TERMS_OF_USE_PATH, PRIVACY_POLICY_PATH, GDPR_POLICY_PATH } from '../url_helpers';
import CsrfForm from '../csrf_form';

import { IUserContext, withUserContext } from './user_context_provider';
import FormGroupWithCheckbox from './form_group_with_checkbox';
import FormGroupWithInput from './form_group_with_input';
import FacebookSignin from './facebook_signin';

interface Props extends IUserContext {
  onToggle: { (event: any): void };
  redirectTo?: string;
  shouldRenderHeader?: boolean;
}
interface State {
  emailValue: string;
  emailConfirmValue: string;
  invalid: boolean;
}

export class SignupForm extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      emailValue: '',
      emailConfirmValue: '',
      invalid: undefined,
    };
  }

  static defaultProps: Partial<Props> = {
    shouldRenderHeader: true,
  };

  get gdpr() {
    return this.props.user.gdpr;
  }

  emailsMatchError() {
    if (this.state.invalid) {
      return I18n.t('commons.signupForm.emailMatchError');
    }
  }

  emailsMatch() {
    // if the user hasn't entered values in the email fields
    // we don't want to annoy them with errors yet
    if (!this.state.emailValue || !this.state.emailConfirmValue) return true;
    return this.state.emailValue === this.state.emailConfirmValue;
  }

  validate() {
    this.setState({ invalid: !this.emailsMatch() });
  }

  @bind
  checkInitialEmailState(_evt, data) {
    // this only updates validation on blur on the email confirm field.
    // we don't want errors popping up until
    // they've at least attempted to write values

    this.setState(data, () => {
      if (this.state.invalid === undefined) this.validate();
    });
  }

  @bind
  checkExistingEmailError(data) {
    // this fires on any input change
    // but should only display an error if it's already been set

    this.setState(data, () => {
      if (this.state.invalid !== undefined) this.validate();
    });
  }

  authUrlParams() {
    return qs.stringify({
      redirect_to: this.props.redirectTo,
    });
  }

  renderAcceptTermsLabel() {
    if (this.gdpr) {
      const gdprTranslation = I18n.t('commons.signupForm.gdprAcceptTermsLabel', {
        termsLinkOpen: `<a href='${TERMS_OF_USE_PATH}'>`,
        privacyLinkOpen: `<a href='${PRIVACY_POLICY_PATH}'>`,
        linkClose: '</a>',
      });
      return <SanitizedRender html={gdprTranslation} />;
    }

    const translation = I18n.t('commons.signupForm.acceptTermsLabel', {
      termsLinkOpen: `<a href='${TERMS_OF_USE_PATH}'>`,
      privacyLinkOpen: `<a href='${PRIVACY_POLICY_PATH}'>`,
      linkClose: '</a>',
    });
    return <SanitizedRender html={translation} />;
  }

  /**
   * Because Enzyme doesn't propogate the onSubmit callback testing
   * that I don't muck with these inputs is tough. According to mdn's
   * docs https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox
   *
   * A checkbox will send a value of 'on' or undefined. We don't, nor should we
   * coerece these into truthy values. On the Rails side we'll make sure that 'on'
   * and nil represet the falsy and truthy values that they should.
   *
   * So this will come across as `user[accept_terms]=on` in the FormParams
   * or `user[accept_terms]=` if it isn't set (or the value won't be sent at all)
   *
   * Here is the corresponding Rails PR:
   */
  renderTermsCheckboxes() {
    return (
      <div>
        <FormGroupWithCheckbox
          label={I18n.t('commons.signupForm.newsletterLabel')}
          inputName="user[weekly_newsletter]"
          defaultChecked={!this.gdpr}
        />

        <FormGroupWithCheckbox
          label={this.renderAcceptTermsLabel()}
          inputName="user[accept_terms]"
          defaultChecked={!this.gdpr}
          required
          tagRequired
        />
      </div>
    );
  }

  renderHeader() {
    if (!this.props.shouldRenderHeader) { return null; }

    return (
      <h4 className="signup-signin__header">
        {I18n.t('commons.signupForm.signupHeader')}
      </h4>
    );
  }

  renderTerms() {
    return (
      <div>
        {this.renderTermsCheckboxes()}
      </div>
    );
  }

  renderGdprTerms() {
    return (
      <div className="signup-signin__form__gdpr">
        {this.renderTermsCheckboxes()}
        <input type="hidden" name="user[terms_type]" value="gdpr" />
      </div>
    );
  }

  renderGdprExplanation() {
    const translation = I18n.t('commons.signupForm.gdprReverbProcessesData', {
      gdprPolicyLinkOpen: `<a target='_blank' href='${GDPR_POLICY_PATH}'>`,
      linkClose: '</a>',
    });

    return (
      <div className="signup-signin__form__gdpr__explanation">

        <hr className="mtb-1" />

        <h4 className="signup-signin__form__gdpr__explanation__title">
          {I18n.t('commons.signupForm.gdprHowReverbUsesData')}
        </h4>
        <p className="signup-signin__form__gdpr__explanation__subtext">
          <SanitizedRender html={translation} />
        </p>
      </div>
    );
  }

  render() {
    return (
      <div>
        {this.renderHeader()}
        <CsrfForm
          action={`/signup?${this.authUrlParams()}`}
          method="post"
          data-disable-auto-validation="true" // block forms.js handling
        >
          <div className="g-container scaling-mb-2">
            <div className="g-col-6">
              <FormGroupWithInput
                label={I18n.t('commons.signupForm.firstNameLabel')}
                inputName="user[first_name]"
                autoComplete="given-name"
                autoFocus
                required
              />
            </div>
            <div className="g-col-6">
              <FormGroupWithInput
                label={I18n.t('commons.signupForm.lastNameLabel')}
                inputName="user[last_name]"
                autoComplete="family-name"
                required
              />
            </div>
          </div>

          <FormGroupWithInput
            label={I18n.t('commons.signupForm.emailLabel')}
            inputType="email"
            inputName="user[email]"
            fieldName="emailValue"
            autoComplete="email"
            value={this.state.emailValue}
            updateField={this.checkExistingEmailError}
            required
          />

          <FormGroupWithInput
            label={I18n.t('commons.signupForm.emailConfirmLabel')}
            inputType="email"
            inputName="emailConfirmValue"
            autoComplete="email"
            errorText={this.emailsMatchError()}
            value={this.state.emailConfirmValue}
            updateField={this.checkExistingEmailError}
            onBlur={this.checkInitialEmailState}
            required
          />

          <FormGroupWithInput
            label={I18n.t('commons.signupForm.passwordLabel')}
            helpText={I18n.t('commons.signupForm.passwordTip', { minLength: 8 })}
            inputName="user[password]"
            inputType="password"
            autoComplete="new-password"
            required
          />

          {this.gdpr && this.renderGdprTerms()}

          <div className="scaling-mtb-2">
            <input type="submit"
              value={I18n.t('commons.signupForm.signUp')}
              className="button button--orange width-100"
              disabled={!this.emailsMatch()}
            />
          </div>

          {!this.gdpr && this.renderTerms()}
        </CsrfForm>

        {this.gdpr && this.renderGdprExplanation()}

        <hr className="mtb-2" />

        <div className="signup-signin__form__facebook">
          <FacebookSignin
            signup
            redirectTo={this.props.redirectTo}
          />
        </div>
      </div>
    );
  }
}

export default withUserContext(SignupForm);



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/components/signup_form.tsx